import * as m from '@staiter/i18n';
import type { SettlementLine } from '@staiter/shared';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Decimal } from 'decimal.js-light';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PageHeader } from '~/components/data';
import { PeriodCloseBanner } from '~/components/domain';
import { CounterpartyPicker } from '~/components/domain';
import { CompanyContextBanner } from '~/components/domain/CompanyContextBanner';
import { Button, DatePicker, Input, Select } from '~/components/primitives';
import {
  type SettlementCreateBody,
  createSettlement,
  proposeSettlement,
} from '~/lib/api/settlements';
import styles from './_auth.settlements.new.module.css';

/**
 * S14-FE.9 — Settlement propose wizard (3-step, editable lines).
 */

export const Route = createFileRoute('/_auth/settlements/new')({ component: SettlementNew });

type LineDraft = {
  key: string;
  direction: 'ar_to_ap' | 'ap_to_ar';
  counterparty_id: string;
  invoice_id: string;
  amount: string;
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

// FE-137 Sprint P+: settlement wizard step labels.
function StepIndicator({ current, total }: { current: number; total: number }): JSX.Element {
  const STEP_LABELS = [
    m.settlement_wizard_step1_label(),
    m.settlement_wizard_step2_label(),
    m.settlement_wizard_step3_label(),
  ];
  return (
    <div className={styles.steps}>
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={i + 1 === current ? styles.stepChipActive : styles.stepChip}>
          {i + 1}. {STEP_LABELS[i] ?? `${i + 1} / ${total}`}
        </span>
      ))}
    </div>
  );
}

function SettlementNew(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [cpId, setCpId] = useState('');
  const [asOf, setAsOf] = useState(today());
  const [date, setDate] = useState(today());
  const [currency, setCurrency] = useState<'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY'>('KZT');
  const [lines, setLines] = useState<LineDraft[]>([]);

  const proposeMut = useMutation({
    mutationFn: () => proposeSettlement(cpId, asOf),
    onSuccess: (r) => {
      const proposed = (r as unknown as { lines?: SettlementLine[] })?.lines ?? [];
      setLines(
        proposed.map((l, i) => ({
          key: `line-${i}-${Date.now()}`,
          direction: l.direction,
          counterparty_id: l.counterparty_id,
          invoice_id: l.invoice_id,
          amount: l.amount.amount,
        })),
      );
      setStep(2);
    },
  });

  const createMut = useMutation({
    mutationFn: (body: SettlementCreateBody) => createSettlement(body),
    onSuccess: (r) => void navigate({ to: '/settlements/$id', params: { id: r.id } }),
  });

  const netAmount = useMemo(() => {
    let arTotal = new Decimal(0);
    let apTotal = new Decimal(0);
    for (const ln of lines) {
      if (ln.direction === 'ar_to_ap') arTotal = arTotal.plus(new Decimal(ln.amount || '0'));
      else apTotal = apTotal.plus(new Decimal(ln.amount || '0'));
    }
    return arTotal.minus(apTotal);
  }, [lines]);

  const submit = () => {
    const body: SettlementCreateBody = {
      settlement_date: date,
      lines: lines.map((l, i) => ({
        line_no: i + 1,
        direction: l.direction,
        counterparty_id: l.counterparty_id || cpId,
        invoice_id: l.invoice_id,
        amount: { amount: l.amount, currency },
        currency,
      })),
      net_amount: { amount: netAmount.toString(), currency },
      currency,
    };
    createMut.mutate(body);
  };

  return (
    <div>
      <PageHeader title={m.settlement_button_new()} />
      <CompanyContextBanner />
      <PeriodCloseBanner scope="settlement" />
      <StepIndicator current={step} total={3} />

      {step === 1 && (
        <div>
          <h2 className={styles.heading}>{m.settlement_column_counterparty()}</h2>
          <div className={styles.formGrid2}>
            {/* FE-138 Sprint Q+: CounterpartyPicker primitive. */}
            <CounterpartyPicker
              label={m.settlement_column_counterparty()}
              required
              value={cpId}
              onChange={(id) => setCpId(id)}
            />
            <DatePicker
              label={m.settlement_column_date()}
              required
              value={date}
              onChange={(next) => setDate(next ?? today())}
            />
            <DatePicker
              label={m.settlement_as_of_label()}
              hint={m.settlement_as_of_hint()}
              required
              value={asOf}
              onChange={(next) => setAsOf(next ?? today())}
            />
            <Select
              label={m.cp_column_currency()}
              value={currency}
              onChange={(e) => setCurrency(e.target.value as 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY')}
              options={[
                { value: 'KZT', label: 'KZT' },
                { value: 'USD', label: 'USD' },
                { value: 'EUR', label: 'EUR' },
                { value: 'RUB', label: 'RUB' },
                { value: 'CNY', label: 'CNY' },
              ]}
            />
          </div>
          <div className={styles.actionsRow}>
            <Button
              variant="primary"
              onClick={() => proposeMut.mutate()}
              loading={proposeMut.isPending}
              disabled={!cpId}
            >
              {m.settlement_button_propose()}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setLines([
                  {
                    key: `line-0-${Date.now()}`,
                    direction: 'ar_to_ap',
                    counterparty_id: cpId,
                    invoice_id: '',
                    amount: '0',
                  },
                ]);
                setStep(2);
              }}
              disabled={!cpId}
            >
              {m.wizard_button_skip_to_manual()}
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <h2 className={styles.heading}>{m.wizard_section_settlement_lines()}</h2>
          {lines.length === 0 && (
            <div className={styles.empty}>{m.wizard_settlement_propose_empty()}</div>
          )}
          {lines.map((ln, idx) => (
            <div key={ln.key} className={styles.lineRow}>
              <Select
                label="Направление"
                value={ln.direction}
                onChange={(e) => {
                  const copy = [...lines];
                  copy[idx] = {
                    ...copy[idx]!,
                    direction: e.target.value as 'ar_to_ap' | 'ap_to_ar',
                  };
                  setLines(copy);
                }}
                options={[
                  { value: 'ar_to_ap', label: 'AR → AP' },
                  { value: 'ap_to_ar', label: 'AP → AR' },
                ]}
              />
              <Input
                label="Invoice UUID"
                value={ln.invoice_id}
                onChange={(e) => {
                  const copy = [...lines];
                  copy[idx] = { ...copy[idx]!, invoice_id: e.target.value };
                  setLines(copy);
                }}
              />
              <CounterpartyPicker
                label={m.settlement_column_counterparty()}
                value={ln.counterparty_id}
                onChange={(id) => {
                  const copy = [...lines];
                  copy[idx] = { ...copy[idx]!, counterparty_id: id };
                  setLines(copy);
                }}
              />
              <Input
                label="Сумма"
                value={ln.amount}
                onChange={(e) => {
                  const copy = [...lines];
                  copy[idx] = { ...copy[idx]!, amount: e.target.value };
                  setLines(copy);
                }}
              />
              <Button
                variant="ghost"
                aria-label="Удалить строку"
                onClick={() => setLines(lines.filter((_, i) => i !== idx))}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            leadingIcon={<Plus size={16} />}
            onClick={() =>
              setLines([
                ...lines,
                {
                  key: `line-${lines.length}-${Date.now()}`,
                  direction: 'ar_to_ap',
                  counterparty_id: cpId,
                  invoice_id: '',
                  amount: '0',
                },
              ])
            }
          >
            {m.wizard_button_add_settlement_line()}
          </Button>
          <div className={styles.netCard}>
            Net amount:{' '}
            <strong>
              {netAmount.toString()} {currency}
            </strong>
          </div>
          <div className={styles.actionsRow}>
            <Button variant="ghost" onClick={() => setStep(1)}>
              {m.common_button_back()}
            </Button>
            <Button variant="primary" onClick={() => setStep(3)} disabled={lines.length < 2}>
              {m.common_button_next()}
            </Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <h2 className={styles.heading}>{m.wizard_section_confirm()}</h2>
          <div className={styles.summaryCard}>
            <div>
              {m.settlement_column_counterparty()}: <strong>{cpId.slice(0, 8)}</strong>
            </div>
            <div>
              {m.settlement_column_date()}: <strong>{date}</strong>
            </div>
            <div>
              {m.wizard_summary_lines_count()} <strong>{lines.length}</strong>
            </div>
            <div>
              Net:{' '}
              <strong className={styles.tabular}>
                {netAmount.toString()} {currency}
              </strong>
            </div>
          </div>
          <div className={styles.actionsRowFlat}>
            <Button variant="ghost" onClick={() => setStep(2)}>
              {m.common_button_back()}
            </Button>
            <Button variant="primary" onClick={submit} loading={createMut.isPending}>
              {m.common_button_create()}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
